import {ComponentType, ReactNode} from 'react';
import {view} from '@sail/ui';
import type {InlineDataProps} from '@stripe-internal/inline-data';
import {InlineDataComponent} from '@stripe-internal/inline-data';
import {ChatRow} from './ChatRow';
import {ChatBubble} from './ChatBubble';

type InlineDataRowProps = {
  inlineData: InlineDataProps;
  position?: 'start' | 'end';
  style?: 'bubble' | 'outer_bubble' | 'none';
  avatar?: ReactNode | null | undefined;
  background?: 'neutral100' | 'neutral600';
  error?: ReactNode;
  children?: ReactNode;
};

export type Props = InlineDataRowProps & {
  ChatRowComponent?: ComponentType<{
    position?: InlineDataRowProps['position'];
    avatar?: InlineDataRowProps['avatar'];
    error?: InlineDataRowProps['error'];
    children: ReactNode;
  }>;
  ChatBubbleComponent?: ComponentType<{
    background: NonNullable<InlineDataRowProps['background']>;
    children: ReactNode;
  }>;
};

export const InlineDataRow = ({
  inlineData,
  style = 'bubble',
  position,
  avatar,
  background = 'neutral100',
  error,
  children,
  ChatRowComponent = ChatRow,
  ChatBubbleComponent = ChatBubble,
}: Props) => {
  let component;
  switch (style) {
    case 'bubble':
      component = (
        <ChatRowComponent avatar={avatar} position={position} error={error}>
          <ChatBubbleComponent background={background}>
            <InlineDataComponent {...inlineData} />
            {children}
          </ChatBubbleComponent>
        </ChatRowComponent>
      );
      break;
    case 'outer_bubble':
      component = (
        <ChatRowComponent avatar={avatar} position={position} error={error}>
          <InlineDataComponent {...inlineData} />
          {children}
        </ChatRowComponent>
      );
      break;
    case 'none':
    default:
      component = (
        <>
          <InlineDataComponent {...inlineData} />
          {children}
        </>
      );
  }

  return <view.div data-testid="inline-data-row">{component}</view.div>;
};
